import React, { useState } from 'react';
import { 
  PlusCircle, 
  Trash, 
  ChevronsUpDown, 
  GripVertical,
  CheckSquare, 
  Image
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from '@/components/ui/dialog';
import CustomImageViewer from "@/components/ui/CustomImageViewer";
import { QuizData } from '../content-editor/types';
import { generateUuidV7 } from '@/utils/uuid';

// Helper function to generate proper GUIDs instead of short IDs
const generateId = () => generateUuidV7();

interface QuizFormProps {
  initialQuiz: QuizData;
  onChange: (quiz: QuizData) => void;
}

export const QuizForm: React.FC<QuizFormProps> = ({ initialQuiz, onChange }) => {
  const [quiz, setQuiz] = useState<QuizData>(initialQuiz);
  const [openQuestions, setOpenQuestions] = useState<Record<string, boolean>>({});
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);

  // Add a new question
  const addQuestion = () => {
    const newQuestion = {
      id: generateId(),
      question: '',
      options: [
        { id: generateId(), text: '', isCorrect: false },
        { id: generateId(), text: '', isCorrect: false },
      ],
    };
    
    const updatedQuiz = {
      ...quiz,
      questions: [...quiz.questions, newQuestion],
    };
    
    setQuiz(updatedQuiz);
    setOpenQuestions({
      ...openQuestions,
      [newQuestion.id]: true,
    });
    
    onChange(updatedQuiz);
  };

  // Delete a question
  const deleteQuestion = (questionId: string) => {
    const updatedQuiz = {
      ...quiz,
      questions: quiz.questions.filter((q) => q.id !== questionId),
    };
    
    setQuiz(updatedQuiz);
    
    // Remove from open questions
    const { [questionId]: _, ...restOpenQuestions } = openQuestions;
    setOpenQuestions(restOpenQuestions);
    
    onChange(updatedQuiz);
  };

  // Update a question
  const updateQuestion = (questionId: string, question: string) => {
    const updatedQuiz = {
      ...quiz,
      questions: quiz.questions.map((q) =>
        q.id === questionId ? { ...q, question } : q
      ),
    };
    
    setQuiz(updatedQuiz);
    onChange(updatedQuiz);
  };

  // Add an option to a question
  const addOption = (questionId: string) => {
    const updatedQuiz = {
      ...quiz,
      questions: quiz.questions.map((q) =>
        q.id === questionId
          ? {
              ...q,
              options: [
                ...q.options,
                { id: generateId(), text: '', isCorrect: false },
              ],
            }
          : q
      ),
    };
    
    setQuiz(updatedQuiz);
    onChange(updatedQuiz);
  };

  // Delete an option
  const deleteOption = (questionId: string, optionId: string) => {
    const updatedQuiz = {
      ...quiz,
      questions: quiz.questions.map((q) =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.filter((o) => o.id !== optionId),
            }
          : q
      ),
    };
    
    setQuiz(updatedQuiz);
    onChange(updatedQuiz);
  };

  // Update an option
  const updateOption = (
    questionId: string,
    optionId: string,
    text: string,
    isCorrect?: boolean
  ) => {
    const updatedQuiz = {
      ...quiz,
      questions: quiz.questions.map((q) =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.map((o) =>
                o.id === optionId
                  ? {
                      ...o,
                      text,
                      ...(isCorrect !== undefined && { isCorrect }),
                    }
                  : o
              ),
            }
          : q
      ),
    };
    
    setQuiz(updatedQuiz);
    onChange(updatedQuiz);
  };

  // Toggle open/close a question
  const toggleQuestion = (questionId: string) => {
    setOpenQuestions({
      ...openQuestions,
      [questionId]: !openQuestions[questionId],
    });
  };

  // Insert image into question text
  const insertImage = () => {
    if (!imageUrl || !activeQuestionId) return;
    
    const imageMarkdown = `![${imageAlt || 'Image'}](${imageUrl})`;
    
    // Find the current question
    const currentQuestion = quiz.questions.find(q => q.id === activeQuestionId);
    if (!currentQuestion) return;
    
    // Insert the image markdown at the end of the current question text
    const updatedQuestion = currentQuestion.question + ' ' + imageMarkdown;
    
    // Update the question
    updateQuestion(activeQuestionId, updatedQuestion);
    
    // Reset image dialog state
    setImageUrl('');
    setImageAlt('');
    setActiveQuestionId(null);
  };

  return (
    <div className="space-y-6 pt-4">
      {quiz.questions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <CheckSquare className="mb-2 h-12 w-12 text-muted-foreground" />
          <h3 className="text-lg font-medium">No questions yet</h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Create your first quiz question to test student understanding
          </p>
          <Button onClick={addQuestion}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Question
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {quiz.questions.map((question, questionIndex) => (
            <Collapsible
              key={question.id}
              open={openQuestions[question.id]}
              onOpenChange={() => toggleQuestion(question.id)}
              className="rounded-md border bg-card text-card-foreground shadow-sm"
            >
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {questionIndex + 1}
                  </div>
                  <div className="font-medium">
                    {question.question || 'Untitled Question'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteQuestion(question.id)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <ChevronsUpDown className="h-4 w-4" />
                    </Button>
                  </CollapsibleTrigger>
                </div>
              </div>
              
              <CollapsibleContent className="border-t px-4 pb-4 pt-3">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor={`question-${question.id}`}>Question</Label>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setActiveQuestionId(question.id)}
                          >
                            <Image className="mr-2 h-3 w-3" />
                            Add Image
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Insert Image</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="imageAlt">Alt Text</Label>
                              <Input 
                                id="imageAlt" 
                                value={imageAlt} 
                                onChange={(e) => setImageAlt(e.target.value)}
                                placeholder="Description of the image"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="imageUrl">Image URL</Label>
                              <Input 
                                id="imageUrl" 
                                value={imageUrl} 
                                onChange={(e) => setImageUrl(e.target.value)}
                                placeholder="https://example.com/image.jpg"
                              />
                            </div>
                            
                            {imageUrl && (
                              <div className="space-y-2">
                                <Label>Preview</Label>
                                <div className="overflow-hidden rounded-md border bg-muted p-2">
                                  <CustomImageViewer 
                                    src={imageUrl} 
                                    alt={imageAlt || 'Preview'}
                                  />
                                </div>
                              </div>
                            )}
                            
                            <div className="flex justify-end gap-2">
                              <DialogClose asChild>
                                <Button variant="outline">Cancel</Button>
                              </DialogClose>
                              <DialogClose asChild>
                                <Button onClick={insertImage}>Insert</Button>
                              </DialogClose>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <Input
                      id={`question-${question.id}`}
                      value={question.question}
                      onChange={(e) => updateQuestion(question.id, e.target.value)}
                      placeholder="Enter your question..."
                    />
                    
                    {question.question.includes('![') && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        <p>This question contains embedded images.</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Options</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addOption(question.id)}
                      >
                        <PlusCircle className="mr-2 h-3 w-3" />
                        Add Option
                      </Button>
                    </div>
                    
                    {question.options.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No options yet</p>
                    ) : (
                      <div className="space-y-2">
                        {question.options.map((option, optionIndex) => (
                          <div
                            key={option.id}
                            className="flex items-center gap-3 rounded-md border p-3"
                          >
                            <div className="flex h-6 w-6 items-center justify-center">
                              <GripVertical className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <Checkbox
                              id={`option-${option.id}-correct`}
                              checked={option.isCorrect}
                              onCheckedChange={(checked) =>
                                updateOption(
                                  question.id,
                                  option.id,
                                  option.text,
                                  checked as boolean
                                )
                              }
                            />
                            <Input
                              id={`option-${option.id}-text`}
                              value={option.text}
                              onChange={(e) =>
                                updateOption(question.id, option.id, e.target.value)
                              }
                              placeholder={`Option ${optionIndex + 1}`}
                              className="flex-grow"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteOption(question.id, option.id)}
                              disabled={question.options.length <= 2}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
          
          <Button onClick={addQuestion} className="w-full">
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Question
          </Button>
        </div>
      )}
    </div>
  );
};
